import { Select } from '@round/ui-kit';
import { InfluencerLocationOption, useInfluencerLocationSelect } from '@/hooks/useInfluencerLocationSelect';
import { waveSelectStyles } from './waveSelectStyles';

type Option = InfluencerLocationOption | null;

export type LocationValue = {
    country: Option;
    state: Option;
    city: Option;
};

export const emptyLocationValue: LocationValue = { country: null, state: null, city: null };

export const locationIdOf = (value: LocationValue): number | null =>
    value.city?.value ?? value.state?.value ?? value.country?.value ?? null;

type LocationSelectProps = {
    inputId: string;
    type: 'country' | 'state' | 'city';
    placeholder: string;
    value: Option;
    onChange: (option: Option) => void;
    country_code?: string;
    state?: string;
};

// Turn an ISO country code (e.g. "US") into its flag emoji.
export const flagEmoji = (countryCode: string): string => {
    if (!countryCode || countryCode.length !== 2) {
        return '';
    }
    return countryCode.toUpperCase().replace(/[A-Z]/g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
};

// The shared option label is "Country, City, State". Each picker only needs the
// part it's choosing: country (with flag), just the state, or just the city.
const formatOptionLabelFor = (type: LocationSelectProps['type']) => {
    if (type === 'country') {
        return (option: InfluencerLocationOption) => `${flagEmoji(option.countryCode)} ${option.country}`;
    }
    if (type === 'state') {
        return (option: InfluencerLocationOption) => option.state || option.label;
    }
    return (option: InfluencerLocationOption) => option.city || option.label;
};

const LocationSelect = ({ inputId, type, placeholder, value, onChange, country_code, state }: LocationSelectProps) => {
    const { selectProps } = useInfluencerLocationSelect({
        type,
        country_code,
        state,
        ordering: 'country_name',
        // Load the full (small) country list in one page rather than paginating it.
        pageSize: type === 'country' ? 300 : undefined,
    });
    return (
        <div translate="no" className="notranslate">
            <Select
                {...selectProps}
                menuPosition="fixed"
                captureMenuScroll={false}
                styles={waveSelectStyles}
                formatOptionLabel={formatOptionLabelFor(type)}
                inputId={inputId}
                placeholder={placeholder}
                value={value}
                onChange={(option) => onChange((option as Option) ?? null)}
            />
        </div>
    );
};

type CountrySelectProps = {
    inputId: string;
    placeholder: string;
    value: Option;
    onChange: (value: Option) => void;
};

/** Standalone country picker over country Location rows (no state/city cascade). */
export const CountrySelect = ({ inputId, placeholder, value, onChange }: CountrySelectProps) => (
    <LocationSelect inputId={inputId} type="country" placeholder={placeholder} value={value} onChange={onChange} />
);

type CountryMultiSelectProps = {
    inputId: string;
    placeholder: string;
    value: InfluencerLocationOption[];
    onChange: (value: InfluencerLocationOption[]) => void;
};

/** Multi-select over country Location rows (e.g. top audience territories). */
export const CountryMultiSelect = ({ inputId, placeholder, value, onChange }: CountryMultiSelectProps) => {
    const { selectProps } = useInfluencerLocationSelect<true>({
        type: 'country',
        ordering: 'country_name',
        // Load the full (small) country list in one page rather than paginating it.
        pageSize: 300,
    });
    return (
        <div translate="no" className="notranslate">
            <Select
                {...selectProps}
                isMulti
                // The menu is portaled, so an absolute menu scrolls the page on
                // open and re-places after every pick while multi keeps it open.
                menuPosition="fixed"
                captureMenuScroll={false}
                styles={waveSelectStyles}
                formatOptionLabel={formatOptionLabelFor('country')}
                inputId={inputId}
                placeholder={placeholder}
                value={value}
                onChange={(options) => onChange((options as InfluencerLocationOption[]) ?? [])}
            />
        </div>
    );
};

type Props = {
    value: LocationValue;
    onChange: (value: LocationValue) => void;
};

/**
 * Cascading country → (US) state → optional city picker over existing Location rows.
 * Controlled by the parent so selections survive step navigation (Back/Next).
 * The most-specific selected PK (city ?? state ?? country) is what gets submitted.
 */
export const LocationStep = ({ value, onChange }: Props) => {
    const { country, state, city } = value;
    const isUnitedStates = country?.countryCode === 'US';

    return (
        <div className="flex flex-col gap-4">
            <div className="wm-field">
                <label className="wm-field__label" htmlFor="location-country">
                    <span>
                        Country <span className="text-wm-danger">*</span>
                    </span>
                </label>
                <LocationSelect
                    inputId="location-country"
                    type="country"
                    placeholder="Select your country"
                    value={country}
                    onChange={(option) => onChange({ country: option, state: null, city: null })}
                />
            </div>

            {country && isUnitedStates && (
                <div className="wm-field">
                    <label className="wm-field__label" htmlFor="location-state">
                        State (optional)
                    </label>
                    <LocationSelect
                        key={`state-${country.value}`}
                        inputId="location-state"
                        type="state"
                        country_code="US"
                        placeholder="Select your state"
                        value={state}
                        onChange={(option) => onChange({ country, state: option, city: null })}
                    />
                </div>
            )}

            {country && (
                <div className="wm-field">
                    <label className="wm-field__label" htmlFor="location-city">
                        City (optional)
                    </label>
                    <LocationSelect
                        key={`city-${state?.value ?? country.value}`}
                        inputId="location-city"
                        type="city"
                        country_code={country.countryCode}
                        state={state?.state}
                        placeholder="Select your city"
                        value={city}
                        onChange={(option) => onChange({ country, state, city: option })}
                    />
                    <span className="wm-field__hint">Used to invite you to live events near you.</span>
                </div>
            )}
        </div>
    );
};
